/**
 * Organization Context for managing organization state across the application
 * Provides global organization data, user role, and organization-related actions
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { organizationService } from '../services/organizationService';
import { clearAllAppCaches } from '../utils/cacheManager';
import {
  Organization,
  OrganizationRole,
  CreateOrganizationInput,
  CreateOrganizationResponse,
  JoinOrganizationInput,
  JoinOrganizationResponse,
  CreateInviteCodeInput,
  CreateInviteCodeResponse,
  OnboardingStatusResponse,
  UserOrganizationMembership
} from '../types/organization';

interface OrganizationContextType {
  // State
  organization: Organization | null;
  role: OrganizationRole | null;
  userOrganizations: UserOrganizationMembership[];
  loading: boolean;
  error: string | null;
  needsOnboarding: boolean;
  switchingOrganization: boolean;
  
  // Actions
  loadOrganization: () => Promise<void>;
  loadUserOrganizations: () => Promise<void>;
  switchOrganization: (organizationId: string) => Promise<void>;
  createOrganization: (input: CreateOrganizationInput) => Promise<CreateOrganizationResponse>;
  joinOrganization: (input: JoinOrganizationInput) => Promise<JoinOrganizationResponse>;
  createInviteCode: (input?: CreateInviteCodeInput) => Promise<CreateInviteCodeResponse>;
  checkOnboardingStatus: () => Promise<OnboardingStatusResponse>;
  clearError: () => void;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

export const OrganizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [role, setRole] = useState<OrganizationRole | null>(null);
  const [userOrganizations, setUserOrganizations] = useState<UserOrganizationMembership[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [switchingOrganization, setSwitchingOrganization] = useState(false);

  // Load organization data when user changes
  const loadOrganization = useCallback(async () => {
    if (!user) {
      console.log('[OrganizationContext] No user, clearing organization state');
      setOrganization(null);
      setRole(null);
      setUserOrganizations([]);
      setNeedsOnboarding(false);
      setLoading(false);
      return;
    }

    console.log('[OrganizationContext] Loading organization for user:', user.uid);
    setLoading(true);
    setError(null);

    try {
      // First check onboarding status
      const onboardingStatus = await organizationService.checkOnboardingStatus();
      console.log('[OrganizationContext] Onboarding status:', onboardingStatus);

      if (onboardingStatus.hasOrganization && onboardingStatus.organization) {
        setOrganization(onboardingStatus.organization);
        setRole((onboardingStatus.role as OrganizationRole) || null);
        setNeedsOnboarding(false);
        console.log('[OrganizationContext] Organization loaded:', {
          org: onboardingStatus.organization.displayName,
          role: onboardingStatus.role
        });
      } else {
        setOrganization(null);
        setRole(null);
        setNeedsOnboarding(onboardingStatus.needsOnboarding);
        console.log('[OrganizationContext] No organization found, needs onboarding:', onboardingStatus.needsOnboarding);
      }
    } catch (err: any) {
      console.error('[OrganizationContext] Failed to load organization:', err);
      setError(err.message || 'Failed to load organization');
      setOrganization(null);
      setRole(null);
      setNeedsOnboarding(false);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Load organization on user change
  useEffect(() => {
    loadOrganization();
  }, [loadOrganization]);

  // Create organization action
  const createOrganization = useCallback(async (input: CreateOrganizationInput): Promise<CreateOrganizationResponse> => {
    console.log('[OrganizationContext] Creating organization:', input.name);
    setLoading(true);
    setError(null);

    try {
      const response = await organizationService.createOrganization(input);
      
      // Update context state
      setOrganization(response.organization);
      setRole(response.userOrganization.role as OrganizationRole);
      setNeedsOnboarding(false);
      
      console.log('[OrganizationContext] Organization created successfully');
      return response;
    } catch (err: any) {
      console.error('[OrganizationContext] Failed to create organization:', err);
      setError(err.message || 'Failed to create organization');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Join organization action
  const joinOrganization = useCallback(async (input: JoinOrganizationInput): Promise<JoinOrganizationResponse> => {
    console.log('[OrganizationContext] Joining organization with code:', input.inviteCode);
    setLoading(true);
    setError(null);

    try {
      const response = await organizationService.joinOrganization(input);
      
      // Update context state
      setOrganization(response.organization);
      setRole(response.role as OrganizationRole);
      setNeedsOnboarding(false);
      
      console.log('[OrganizationContext] Successfully joined organization');
      return response;
    } catch (err: any) {
      console.error('[OrganizationContext] Failed to join organization:', err);
      setError(err.message || 'Failed to join organization');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create invite code action
  const createInviteCode = useCallback(async (input: CreateInviteCodeInput = {}): Promise<CreateInviteCodeResponse> => {
    console.log('[OrganizationContext] Creating invite code');
    
    if (!organization) {
      throw new Error('No organization available to create invite code');
    }

    if (!role || (role !== 'owner' && role !== 'admin')) {
      throw new Error('Insufficient permissions to create invite codes');
    }

    try {
      const response = await organizationService.createInviteCode(input);
      console.log('[OrganizationContext] Invite code created successfully');
      return response;
    } catch (err: any) {
      console.error('[OrganizationContext] Failed to create invite code:', err);
      setError(err.message || 'Failed to create invite code');
      throw err;
    }
  }, [organization, role]);

  // Check onboarding status action
  const checkOnboardingStatus = useCallback(async (): Promise<OnboardingStatusResponse> => {
    console.log('[OrganizationContext] Checking onboarding status');
    
    try {
      const response = await organizationService.checkOnboardingStatus();
      
      // Update state based on response
      setNeedsOnboarding(response.needsOnboarding);
      
      if (response.hasOrganization && response.organization) {
        setOrganization(response.organization);
        setRole((response.role as OrganizationRole) || null);
      }
      
      return response;
    } catch (err: any) {
      console.error('[OrganizationContext] Failed to check onboarding status:', err);
      setError(err.message || 'Failed to check onboarding status');
      throw err;
    }
  }, []);

  // Clear error action
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Load user organizations action
  const loadUserOrganizations = useCallback(async () => {
    if (!user) {
      console.log('[OrganizationContext] No user, clearing user organizations');
      setUserOrganizations([]);
      return;
    }

    console.log('[OrganizationContext] Loading user organizations');
    
    try {
      const memberships = await organizationService.getUserOrganizations();
      
      // Update current organization flags based on current organization
      // Keep the roles from backend as they are authoritative
      const updatedMemberships = memberships.map(membership => ({
        ...membership,
        isCurrentOrganization: organization ? membership.organization.id === organization.id : false
      }));
      
      setUserOrganizations(updatedMemberships);
      console.log('[OrganizationContext] Loaded', updatedMemberships.length, 'user organizations');
    } catch (err: any) {
      console.error('[OrganizationContext] Failed to load user organizations:', err);
      setError(err.message || 'Failed to load organizations');
    }
  }, [user, organization]);

  // Switch organization action
  const switchOrganization = useCallback(async (organizationId: string) => {
    if (!user) {
      throw new Error('No user available to switch organization');
    }

    if (organization && organization.id === organizationId) {
      console.log('[OrganizationContext] Already on the requested organization');
      return;
    }

    console.log('[OrganizationContext] Switching to organization:', organizationId);
    setSwitchingOrganization(true);
    setError(null);

    try {
      // Clear all application caches before switching
      // This ensures no stale data from the previous organization persists
      clearAllAppCaches();
      console.log('[OrganizationContext] Cleared all caches for organization switch');

      const response = await organizationService.switchCurrentOrganization({ organizationId });
      
      // Update context state
      setOrganization(response.organization);
      
      // Find the role from user organizations or default to member
      const membership = userOrganizations.find(m => m.organization.id === organizationId);
      setRole(membership?.role || 'member');
      
      // Update user organizations to reflect new current organization
      const updatedMemberships = userOrganizations.map(membership => ({
        ...membership,
        isCurrentOrganization: membership.organization.id === organizationId
      }));
      setUserOrganizations(updatedMemberships);
      
      console.log('[OrganizationContext] Successfully switched to organization:', response.organization.displayName);
      
      // Trigger a full reload of organization data to ensure consistency
      await loadOrganization();
    } catch (err: any) {
      console.error('[OrganizationContext] Failed to switch organization:', err);
      setError(err.message || 'Failed to switch organization');
      throw err;
    } finally {
      setSwitchingOrganization(false);
    }
  }, [user, organization, userOrganizations, loadOrganization]);

  const value: OrganizationContextType = {
    // State
    organization,
    role,
    userOrganizations,
    loading,
    error,
    needsOnboarding,
    switchingOrganization,
    
    // Actions
    loadOrganization,
    loadUserOrganizations,
    switchOrganization,
    createOrganization,
    joinOrganization,
    createInviteCode,
    checkOnboardingStatus,
    clearError
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};